/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * Browser'dan timezone bilgisini al
 * @returns IANA timezone string (örn: "Europe/Istanbul", "America/New_York")
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    console.warn('Browser timezone okunamadı, varsayılan kullanılıyor:', e);
    // Fallback: offset'e göre tahmin et
    return getTimezoneFromOffset();
  }
}

/**
 * Browser offset'inden timezone tahmin et (fallback)
 */
function getTimezoneFromOffset(): string {
  const offset = -new Date().getTimezoneOffset() / 60;

  // Yaygın timezone'lar için basit mapping
  const offsetToTimezone: Record<string | number, string> = {
    3: 'Europe/Istanbul',   // UTC+3
    0: 'UTC',               // UTC+0
    1: 'Europe/Paris',      // UTC+1
    2: 'Europe/Berlin',     // UTC+2 (kış)
    4: 'Asia/Dubai',        // UTC+4
    9: 'Asia/Tokyo',        // UTC+9
    8: 'Asia/Singapore',    // UTC+8
    '-5': 'America/New_York',  // UTC-5
    '-6': 'America/Chicago',   // UTC-6
    '-8': 'America/Los_Angeles', // UTC-8
  };

  // Önce sayı olarak dene, sonra string olarak
  const timezone = offsetToTimezone[offset] || offsetToTimezone[String(offset)];
  return timezone || 'Europe/Istanbul';
}

/**
 * Browser timezone bilgisini formatla
 */
export function getBrowserTimezoneInfo(): {
  timezone: string;
  offset: number;
  offsetString: string;
} {
  const timezone = getBrowserTimezone();
  const offset = new Date().getTimezoneOffset() / -60;
  const offsetString = offset >= 0 ? `+${offset}` : `${offset}`;

  return {
    timezone,
    offset,
    offsetString,
  };
}

/**
 * Timezone offset'ini hesapla (UTC'ye göre saat farkı)
 */
export function getTimezoneOffset(timezone: string, date?: Date): string {
  try {
    const targetDate = date || new Date();

    // UTC zamanı
    const utc = new Date(targetDate.toLocaleString('en-US', { timeZone: 'UTC' }));

    // Belirtilen timezone'daki zaman
    const local = new Date(targetDate.toLocaleString('en-US', { timeZone: timezone }));

    // Offset hesapla (milisaniye cinsinden)
    const offsetMs = local.getTime() - utc.getTime();

    // Saat ve dakika cinsinden çevir
    const offsetHours = Math.floor(Math.abs(offsetMs) / (1000 * 60 * 60));
    const offsetMinutes = Math.floor((Math.abs(offsetMs) % (1000 * 60 * 60)) / (1000 * 60));
    const sign = offsetMs >= 0 ? '+' : '-';

    // Format as ±HH:MM
    const hoursStr = offsetHours.toString().padStart(2, '0');
    const minutesStr = offsetMinutes.toString().padStart(2, '0');

    return `${sign}${hoursStr}:${minutesStr}`;
  } catch (e) {
    return '';
  }
}

export interface TimezoneOption {
  value: string;
  label: string;
  searchStr: string;
  offsetMinutes: number;
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Tokyo',
  'Asia/Singapore',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
] as const;

function parseTimezoneOffsetMinutes(offset: string): number {
  if (!offset) return 0;
  const sign = offset.startsWith('-') ? -1 : 1;
  const [hours, minutes] = offset.substring(1).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return sign * ((hours * 60) + minutes);
}

function getSupportedTimezones(): string[] {
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    const supported = intl.supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length > 0) return supported;
  } catch {
    // fallback below
  }
  return [...FALLBACK_TIMEZONES];
}

export function getTimezoneOption(timezone: string, date?: Date): TimezoneOption {
  const offset = getTimezoneOffset(timezone, date);
  const label = offset ? `(UTC${offset}) ${timezone}` : timezone;
  return {
    value: timezone,
    label,
    searchStr: `${timezone} UTC${offset}`.trim(),
    offsetMinutes: parseTimezoneOffsetMinutes(offset),
  };
}

/**
 * Ortak timezone seçenekleri. IANA değerini korur, etikette sayısal UTC offset'i öne çıkarır.
 */
export function getTimezoneOptions(selectedTimezone?: string | null, date?: Date): TimezoneOption[] {
  const selected = String(selectedTimezone || '').trim();
  const values = new Set(['UTC', ...getSupportedTimezones()]);
  if (selected) values.add(selected);

  return Array.from(values)
    .map((timezone) => getTimezoneOption(timezone, date))
    .sort((a, b) => {
      if (a.offsetMinutes !== b.offsetMinutes) return a.offsetMinutes - b.offsetMinutes;
      return a.value.localeCompare(b.value);
    });
}

/**
 * Timezone fallback stratejisi
 * User timezone → Browser timezone → Tenant timezone → Default
 */
export function getEffectiveTimezone(
  userTimezone?: string | null,
  tenantTimezone?: string | null
): string {
  // 1. User timezone varsa kullan
  if (userTimezone) {
    return userTimezone;
  }

  // 2. Browser timezone'u kullan
  const browserTimezone = getBrowserTimezone();
  if (browserTimezone) {
    return browserTimezone;
  }

  // 3. Tenant timezone'u kullan
  if (tenantTimezone) {
    return tenantTimezone;
  }

  // 4. Varsayılan
  return 'Europe/Istanbul';
}
